import { useEmailSuppressionResult } from '@/api/queries/emails/useGetEmailSuppressionResults';
import { AlertProps, TDropdownItem, H2 } from '@payaca/ambrosia';
import * as customerActions from '@payaca/store/customer/customerActions';
import React, {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import './PreviewAndSendSendableDocument.sass';

import { AddCustomerEmailModal } from '..';

import PreviewDocument from '@/ui/components/PreviewQuoteInvoice/PreviewDocument';
import { type SenderChoiceControl } from '@/ui/components/senderChoice/useSenderChoice';

import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { substituteAndSplitEmailTemplate } from '@payaca/helpers/emailSubstitutionHelper';
import { Customer } from '@payaca/types/customerTypes';
import SendableDocumentEmailControl from './SendableDocumentEmailControl';

type Props = {
  documentType: 'invoice' | 'quote' | 'estimate' | 'change proposal';
  title: string;
  documentPreview: React.ReactNode;
  emailConfig: {
    htmlTemplate?: string;
    subject: string;
    bodyTemplate: string;
    substitutions?: { find: string; replaceWith: string }[];
  };
  recipientConfig: {
    customer: Customer;
    contactId?: number;
  };
  /**
   * Adds a From line naming who the email is sent as. Pass it only where the
   * send behind this form honours the user's remembered choice of sender — on
   * any other send it would name a sender the email never uses.
   */
  senderChoice?: SenderChoiceControl;
  onSend: (
    payload: {
      emailCopy: {
        preButton: string;
        postButton: string;
      };
    },
    callback: () => void,
    onErrorCallback: (errorMessages: string[]) => void
  ) => void;
  actions?: TDropdownItem[];
  alerts?: PropsWithChildren<AlertProps>[];
};

const PreviewAndSendSendableDocument: FunctionComponent<Props> = ({
  documentType,
  title,
  documentPreview,
  emailConfig,
  recipientConfig,
  senderChoice,
  onSend,
  actions,
  alerts = [],
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [showAddCustomerEmailModal, setShowAddCustomerEmailModal] =
    useState(false);
  const [isSending, setIsSending] = useState(false);

  const customerContact = useMemo(() => {
    return getJobContactFromCustomer(
      recipientConfig.customer,
      recipientConfig.contactId || null
    );
  }, [recipientConfig]);

  const [sendErrorMessages, setSendErrorMessages] = useState<string[]>();

  const [sendPayload, setSendPayload] = useState<{
    htmlTemplate?: string;
    emailCopy: {
      preButton: string;
      postButton: string;
    };
  }>({
    htmlTemplate: emailConfig.htmlTemplate,
    emailCopy: {
      preButton: '',
      postButton: '',
    },
  });

  useEffect(() => {
    const splitTemplate = substituteAndSplitEmailTemplate(
      emailConfig.bodyTemplate,
      emailConfig.substitutions || []
    );

    setSendPayload((x) => ({
      ...x,
      htmlTemplate: emailConfig.htmlTemplate,
      emailCopy: splitTemplate,
    }));
  }, [
    emailConfig.bodyTemplate,
    emailConfig.htmlTemplate,
    emailConfig.substitutions,
  ]);

  const emailSuppressionResult = useEmailSuppressionResult(
    customerContact?.emailAddress
  );

  const handleSendClick = useCallback(() => {
    setSendErrorMessages([]);
    setIsSending(true);
    if (!customerContact?.emailAddress?.length) {
      setShowAddCustomerEmailModal(true);
      setIsSending(false);
    } else if (emailSuppressionResult?.isSuppressed) {
      setSendErrorMessages([
        `The customer's email is blocked. Reason: ${emailSuppressionResult.reason}`,
      ]);
      setIsSending(false);
    } else if (
      !sendPayload.emailCopy.postButton?.length &&
      !sendPayload.emailCopy.preButton?.length
    ) {
      setSendErrorMessages(['The email must have some content']);
      setIsSending(false);
    } else {
      onSend(
        sendPayload,
        () => {
          setIsSending(false);
        },
        (errorMessages) => {
          setSendErrorMessages(errorMessages);
          setIsSending(false);
        }
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'onSend', and 'emailSuppressionResult.reason'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: sendPayload.emailCopy.preButton
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: sendPayload.emailCopy.postButton
  }, [
    sendPayload,
    customerContact,
    setSendErrorMessages,
    sendPayload.emailCopy.postButton,
    sendPayload.emailCopy.preButton,
    emailSuppressionResult?.isSuppressed,
  ]);

  const sendErrorAlerts: PropsWithChildren<AlertProps>[] =
    sendErrorMessages?.length
      ? [
          {
            colour: 'red',
            children: (
              <>
                <strong>This {documentType} could not be sent:</strong>
                <ul>
                  {sendErrorMessages.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </>
            ),
          },
        ]
      : [];

  return (
    <>
      <PreviewDocument
        title={title}
        firstAction={{
          onClick: () => !isSending && handleSendClick(),
          children: 'Send',
          isProcessing: isSending,
        }}
        actions={actions}
        alerts={[...alerts, ...sendErrorAlerts]}
      >
        <div className="space-y-4">
          <H2>Preview email</H2>
          {customerContact && (
            <SendableDocumentEmailControl
              buttonText={`View ${documentType}`}
              emailSubject={emailConfig.subject}
              senderChoice={senderChoice}
              emailState={sendPayload}
              onChange={(value) => {
                setSendPayload((x) => {
                  return { ...x, ...value };
                });
              }}
              sendToAddress={customerContact.emailAddress}
            />
          )}

          <H2>Preview {documentType}</H2>
          {documentPreview}
        </div>
      </PreviewDocument>

      {recipientConfig.customer && (
        <AddCustomerEmailModal
          customer={recipientConfig.customer}
          isOpen={showAddCustomerEmailModal}
          contactId={recipientConfig.contactId || null}
          onClose={() => setShowAddCustomerEmailModal(false)}
          customerUpdateCallback={() => {
            dispatch(
              customerActions.requestGetCustomer(recipientConfig.customer.id)
            );
            setShowAddCustomerEmailModal(false);
          }}
        />
      )}
    </>
  );
};

export default PreviewAndSendSendableDocument;
